import { useState } from 'react';
import type { EmailRecipient, PickedContact } from '@/lib/apiClient';
import { contactLine, type JobContactOption } from './jobContacts';

/**
 * Who an RFI or submittal email goes to (#598 step 2): a To line and a CC line,
 * each filled from the job's contacts or typed. The assignee or reviewer, when
 * they're a job contact with an email, starts on the To line. What's here when
 * the draft is used is what the send records, once the person confirms it went.
 */

const css = `
  .erc-line { display: flex; flex-direction: column; gap: 4px; }
  .erc-line > label {
    font: var(--ew-fw-medium) var(--ew-fs-caption)/1 var(--ew-font);
    text-transform: uppercase; letter-spacing: var(--ew-ls-caption); color: var(--ew-n-700);
  }
  .erc-box {
    display: flex; flex-wrap: wrap; align-items: center; gap: 6px; padding: 6px 8px;
    border: 1px solid var(--ew-n-300); border-radius: var(--ew-r-md); background: #fff; min-height: 38px;
  }
  .erc-chip {
    display: inline-flex; align-items: center; gap: 6px; padding: 2px 4px 2px 8px; border-radius: var(--ew-r-pill);
    background: var(--ew-n-100); color: var(--ew-dark); font: var(--ew-fw-medium) var(--ew-fs-caption)/1.6 var(--ew-font);
    max-width: 100%;
  }
  .erc-chip span { overflow-wrap: anywhere; }
  .erc-chip button { border: 0; background: none; cursor: pointer; color: var(--ew-n-500); font-size: 14px; line-height: 1; padding: 0 4px; }
  .erc-chip button:hover { color: var(--ew-dark); }
  .erc-type { flex: 1; min-width: 160px; border: 0; outline: 0; font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.5 var(--ew-font); }
  .erc-pick { border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-sm); background: #fff; font: var(--ew-fw-regular) var(--ew-fs-caption) var(--ew-font); padding: 3px 6px; max-width: 100%; }
  .erc-err { color: #8F2414; font: var(--ew-fw-regular) var(--ew-fs-caption)/1.4 var(--ew-font); }
`;

/**
 * A plain address: something@something.tld, and nothing that would break the
 * mailto: link it goes into (?, &, #, a comma, a space).
 */
const EMAIL = /^[^\s@?&#,;<>()"]+@[^\s@?&#,;<>()"]+\.[^\s@?&#,;<>()"]+$/;
export const isPlainEmail = (v: string) => EMAIL.test(v.trim());

/** At most this many on one email: the most a send can record (MAX_RECIPIENTS in the API). */
export const MAX_RECIPIENTS = 30;

/**
 * The assignee or reviewer on the To line to start with, when they're a contact
 * with an email who is still on the job: someone taken off it since is still who
 * it was assigned to, but the draft must not send them the job's papers unasked (review).
 */
export function startingRecipients(contact: PickedContact | null | undefined): EmailRecipient[] {
  return contact?.email && contact.onJob ? [{ kind: 'to', email: contact.email.toLowerCase(), name: contact.name, contactId: contact.id }] : [];
}

/**
 * The last send's recipients, as a reminder or a revision reuses them: anyone
 * picked from the job's contacts is re-read from the job's contacts NOW, so
 * someone taken off the job since is dropped and a corrected address is used,
 * rather than the draft going to whoever and wherever the last one did (review).
 * A typed address has nothing to check against and is kept. `contacts` null is
 * "not known" (they failed to load): the list is kept as it was, in plain view.
 */
export function reusableRecipients(previous: readonly EmailRecipient[], contacts: readonly JobContactOption[] | null): EmailRecipient[] {
  if (!contacts) return [...previous];
  const seen = new Set<string>();
  const out: EmailRecipient[] = [];
  for (const r of previous) {
    let next: EmailRecipient = r;
    if (r.contactId != null) {
      const c = contacts.find((x) => x.contactId === r.contactId);
      if (!c?.email) continue;
      next = { ...r, email: c.email.toLowerCase(), name: c.name };
    }
    // A corrected address can now be one already listed: keep the first (To before CC).
    if (seen.has(next.email.toLowerCase())) continue;
    seen.add(next.email.toLowerCase());
    out.push(next);
  }
  return out;
}

/** Who a send went to, in a sentence: "Ana Architect and Bo Builder, cc Olive Owner". */
export function describeRecipients(list: readonly EmailRecipient[]): string {
  const names = (k: 'to' | 'cc') => list.filter((r) => r.kind === k).map((r) => r.name ?? r.email);
  const join = (xs: string[]) => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);
  const to = names('to');
  const cc = names('cc');
  return [to.length ? join(to) : null, cc.length ? `cc ${join(cc)}` : null].filter(Boolean).join(', ');
}

/**
 * One address as it goes into the link: percent-encoded, '@' kept readable. An
 * address from the address book can be any valid email, and a valid local part
 * may hold &, ?, # or + — raw, an & starts a new field and some mail apps read +
 * as a space, so the draft would arrive addressed to someone else (review).
 */
const linkAddress = (email: string) => encodeURIComponent(email).replace(/%40/gi, '@');

/** The mailto: link: To and CC, each address encoded, subject and body encoded. */
export function mailtoFor(recipients: readonly EmailRecipient[], subject: string, body: string): string {
  const to = recipients.filter((r) => r.kind === 'to').map((r) => linkAddress(r.email)).join(',');
  const cc = recipients.filter((r) => r.kind === 'cc').map((r) => linkAddress(r.email)).join(',');
  const qs = [cc ? `cc=${cc}` : null, `subject=${encodeURIComponent(subject)}`, `body=${encodeURIComponent(body)}`]
    .filter(Boolean).join('&');
  return `mailto:${to}?${qs}`;
}

export default function RecipientsField({ contacts, value, onChange }: {
  contacts: JobContactOption[];
  value: EmailRecipient[];
  onChange: (next: EmailRecipient[]) => void;
}) {
  return (
    <>
      <style>{css}</style>
      <Line kind="to" label="To" contacts={contacts} value={value} onChange={onChange} />
      <Line kind="cc" label="CC" contacts={contacts} value={value} onChange={onChange} />
    </>
  );
}

function Line({ kind, label, contacts, value, onChange }: {
  kind: 'to' | 'cc';
  label: string;
  contacts: JobContactOption[];
  value: EmailRecipient[];
  onChange: (next: EmailRecipient[]) => void;
}) {
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);
  const here = value.filter((r) => r.kind === kind);
  const taken = new Set(value.map((r) => r.email.toLowerCase()));
  const offer = contacts.filter((c) => c.email && !taken.has(c.email.toLowerCase()));

  /**
   * The list, unless it would go past MAX_RECIPIENTS: refused here, before the draft
   * can go out, because the send's record would be refused after it had (review).
   */
  const set = (next: EmailRecipient[]) => {
    if (next.length > MAX_RECIPIENTS) {
      setError(`One email can go to at most ${MAX_RECIPIENTS} people. For more, use a distribution list.`);
      return false;
    }
    onChange(next);
    setError(null);
    return true;
  };

  /** Add an address; one already on either line moves here rather than appearing twice. */
  const add = (r: Omit<EmailRecipient, 'kind'>) =>
    set([...value.filter((x) => x.email.toLowerCase() !== r.email.toLowerCase()), { ...r, kind, email: r.email.toLowerCase() }]);

  const commit = () => {
    const parts = typed.split(/[,;\s]+/).map((p) => p.trim()).filter(Boolean);
    if (!parts.length) { setTyped(''); return; }
    const bad = parts.filter((p) => !isPlainEmail(p));
    if (bad.length) { setError(`${bad[0]} isn’t an email address.`); return; }
    let next = value;
    for (const p of parts) {
      const email = p.toLowerCase();
      const known = contacts.find((c) => c.email?.toLowerCase() === email);
      next = [...next.filter((x) => x.email.toLowerCase() !== email), { kind, email, name: known?.name ?? null, contactId: known?.contactId ?? null }];
    }
    if (set(next)) setTyped('');
  };

  return (
    <div className="erc-line">
      <label htmlFor={`erc-${kind}`}>{label}</label>
      <div className="erc-box">
        {here.map((r) => (
          <span key={r.email} className="erc-chip">
            <span>{r.name ? `${r.name} <${r.email}>` : r.email}</span>
            <button type="button" aria-label={`Remove ${r.name ?? r.email}`} onClick={() => onChange(value.filter((x) => x !== r))}>×</button>
          </span>
        ))}
        <input
          id={`erc-${kind}`}
          className="erc-type"
          type="text"
          inputMode="email"
          value={typed}
          placeholder={here.length ? '' : 'Type an address, or pick from the job’s contacts'}
          onChange={(e) => { setTyped(e.target.value); setError(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',' || e.key === ';') { e.preventDefault(); commit(); } }}
          onBlur={commit}
        />
        {offer.length > 0 && (
          <select
            className="erc-pick"
            aria-label={`Add to ${label} from the job’s contacts`}
            value=""
            onChange={(e) => {
              const c = offer.find((x) => String(x.contactId) === e.target.value);
              if (c?.email) add({ email: c.email, name: c.name, contactId: c.contactId });
            }}
          >
            <option value="">＋ From job contacts</option>
            {offer.map((c) => <option key={c.contactId} value={c.contactId}>{contactLine(c)} — {c.roles}</option>)}
          </select>
        )}
      </div>
      {error && <span className="erc-err" role="alert">{error}</span>}
    </div>
  );
}
